/**
 * Subscription API Service
 * Contains helper functions for transforming subscription plan data
 */

import {SubscriptionPlanDetails, SubscriptionFeature, PlusOneGuestInfo} from '~types/subscription/subscription.types';

// API response types
export interface ApiSubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  stripe_price_id: string;
  is_custom: boolean;
  is_active: boolean;
  feature_list: {
    unlimited_dinners?: boolean;
    priority_matching?: boolean;
    culture_edit_events?: boolean;
    cancel_anytime?: boolean;
    monthly_pyxi_party?: number;
    bring_plus_one_fee?: string | number; // '£2', 'Free', etc.
    last_minute_join_hours?: number;
    best_match_event?: {
      enabled: boolean;
      min_dinners_required?: number;
    };
    no_show_fee?: {
      dinner?: string;
      culture_edit?: string;
      pyxi_party?: string;
    };
    display_text: string;
  };
}

// Feature mapping to convert API features to UI features
const FEATURE_ICON_MAP: Record<string, string> = {
  unlimited_dinners: '🍽️',
  priority_matching: '⭐',
  culture_edit_events: '🎭',
  monthly_pyxi_party: '🎉',
  reduced_no_show_fee: '💷',
  no_show_fee_waiver: '💷',
  cancel_anytime: '✋',
  bring_plus_one: '👥',
  last_minute_join: '⏰',
};

const FEATURE_NAME_MAP: Record<string, string> = {
  unlimited_dinners: 'Unlimited dinners',
  culture_edit_events: 'Unlimited Cultural Events',
  monthly_pyxi_party: 'Pyxi Housewarming Party',
  priority_matching: 'Free event with your 5 best matches (at least 2 event attendance required)',
  reduced_no_show_fee: 'Reduced no-show fee (£3 vs £6)',
  no_show_fee_waiver: 'No-show fee waiver',
  cancel_anytime: 'Cancel anytime',
  bring_plus_one: 'Bring a +1 for only £2',
  last_minute_join: 'Join a dinner last minute (up to 2 hours before the event)',
  best_match_event: 'Free event with your 5 best matches',
};

const FEATURE_DESCRIPTION_MAP: Record<string, string> = {
  unlimited_dinners: 'Enjoy unlimited dinner events',
  priority_matching: 'Get matched with compatible users first',
  culture_edit_events: 'Access unlimited cultural events',
  monthly_pyxi_party: 'Exclusive monthly housewarming party access',
  reduced_no_show_fee: '£3 vs £6',
  no_show_fee_waiver: 'Waived no-show fee for all events',
  cancel_anytime: 'No long-term commitment',
  bring_plus_one: 'Bring a friend to dinners and events',
  last_minute_join: 'Up to 2 hours before the event',
  best_match_event: 'At least 2 event attendance required',
};

/**
 * Extract features from API plan response
 */
export function extractFeaturesFromApiPlan(apiPlan: ApiSubscriptionPlan): SubscriptionFeature[] {
  const features: SubscriptionFeature[] = [];
  const featureList = apiPlan.feature_list;
  featureList.cancel_anytime = true; 
  

  // Define the feature order to match the static UI
  const featureOrder = [
    'unlimited_dinners',
    'culture_edit_events',
    'monthly_pyxi_party',
    'no_show_fee_waiver',
    'reduced_no_show_fee',
    'last_minute_join',
    'bring_plus_one',
    'best_match_event',
    'cancel_anytime',
  ];

  // Add included features in the defined order
  for (const featureId of featureOrder) {
    let isIncluded = false;
    let description = FEATURE_DESCRIPTION_MAP[featureId] || '';

    if (featureId === 'unlimited_dinners' && featureList.unlimited_dinners) {
      isIncluded = true;
    } else if (featureId === 'priority_matching' && featureList.priority_matching) {
      isIncluded = true;
    } else if (featureId === 'bring_plus_one') {
      if (featureList.bring_plus_one_fee !== undefined) {
        isIncluded = true;
        // Update name and description based on fee
        const feeString = typeof featureList.bring_plus_one_fee === 'string'
          ? featureList.bring_plus_one_fee == 'Free' ? "Free" : `only ${featureList.bring_plus_one_fee}`
          : featureList.bring_plus_one_fee === 0
          ? 'FREE'
          : `only £${featureList.bring_plus_one_fee}`;
        const featureName = `Bring a +1 for ${feeString}`;
        
        features.push({
          id: featureId,
          name: featureName,
          description: 'Invite a friend to any dinner',
          included: true,
          icon: FEATURE_ICON_MAP[featureId],
        });
        continue;
      }
    } else if (featureId === 'last_minute_join' && featureList.last_minute_join_hours) {
      isIncluded = true;
      description = `Up to ${featureList.last_minute_join_hours} hours before the event`;
    } else if (featureId === 'culture_edit_events' && featureList.culture_edit_events) {
      isIncluded = true;
    } else if (featureId === 'monthly_pyxi_party' && featureList.monthly_pyxi_party) {
      isIncluded = true;
    } else if (featureId === 'best_match_event') {
      isIncluded = true; // Traditionally included for all plans
    } else if (featureId === 'reduced_no_show_fee') {
      // Check if this is a reduced fee plan (has specific amounts for no-show fees)
      if (featureList.no_show_fee?.dinner && !featureList.monthly_pyxi_party) {
        isIncluded = true;
      } else {
        // Skip this feature if not included
        continue;
      }
    } else if (featureId === 'no_show_fee_waiver') {
      // Only for all access plans with reduced fees
      if (featureList.no_show_fee?.dinner && featureList.monthly_pyxi_party) {
        isIncluded = true;
      } else {
        // Skip this feature if not included
        continue;
      }
    } else if (featureId === 'cancel_anytime' && featureList.cancel_anytime) {
      isIncluded = true;
    } 

    // Only add features that are explicitly mentioned in the API response
    if (
      featureId === 'unlimited_dinners' ||
      featureId === 'priority_matching' ||
      featureId === 'last_minute_join' ||
      featureId === 'culture_edit_events' ||
      featureId === 'monthly_pyxi_party' ||
      featureId === 'best_match_event' ||
      featureId === 'cancel_anytime' ||
      (featureList.no_show_fee && (featureId === 'reduced_no_show_fee' || featureId === 'no_show_fee_waiver'))
    ) {
      features.push({
        id: featureId,
        name: FEATURE_NAME_MAP[featureId] || featureId,
        description,
        included: isIncluded,
        icon: FEATURE_ICON_MAP[featureId],
      });
    }
  }

  return features;
}

/**
 * Map API plan name to SubscriptionPlan enum value
 */
export function mapApiPlanNameToId(name: string): string {
  if (name.includes('Reset Table') && !name.includes('Culture Edit')) {
    return 'reset_table';
  }
  if (name.includes('Culture Edit')) {
    return 'culture_edit';
  }
  if (name.includes('All Access')) {
    return 'all_access';
  }
  return name.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Get short name for plan
 */
export function getShortName(name: string): string {
  if (name.includes('Reset Table') && !name.includes('Culture Edit')) {
    return 'Reset Table';
  }
  if (name.includes('Culture Edit')) {
    return 'Cultural Edit';
  }
  if (name.includes('All Access')) {
    return 'All Access';
  }
  return name;
}

/**
 * Get description for plan
 */
export function getDescription(name: string): string {
  if (name.includes('Reset Table') && !name.includes('Cultural Edit')) {
    return 'Unlimited dinners with priority matching';
  }
  if (name.includes('Cultural Edit')) {
    return 'Unlimited dinners & cultural events';
  }
  if (name.includes('All Access')) {
    return 'Access to all experiences including our monthly signature party';
  }
  return '';
}

/**
 * Get color for plan
 */
export function getPlanColor(name: string): string {
  // All plans have the same color in the current design
  return '#FF9500';
}

/**
 * Extract +1 guest information from API plan
 * Rules:
 * - £14.99: +1 guest for £2
 * - £21.99: +1 guest free
 * - £31.99: +1 guest free (Pyxi Party excluded)
 */
export function extractPlusOneGuestInfo(apiPlan: ApiSubscriptionPlan): PlusOneGuestInfo | undefined {
  const featureList = apiPlan.feature_list;
  
  // If bring_plus_one_fee is not provided, determine based on price
  if (!featureList.bring_plus_one_fee) {
    return undefined;
  }

  const isFree = featureList.bring_plus_one_fee === 'Free' || featureList.bring_plus_one_fee === 0;
  const hasExclusions = apiPlan.price === 32.99 || apiPlan.price === 31.99; // All Access plan excludes Pyxi Party

  return {
    enabled: true,
    fee: featureList.bring_plus_one_fee,
    description: isFree ? 'Bring a +1 guest for free' : `Bring a +1 guest for ${featureList.bring_plus_one_fee}`,
    registrationDaysRequired: 3, // Standard requirement: 3 days before event
    excludedEventTypes: hasExclusions ? ['HOUSEWARMING'] : undefined, // Pyxi Party (HOUSEWARMING) excluded for All Access
  };
}


/**
 * Sort plans by the expected order
 */
export function sortPlansByOrder(plans: SubscriptionPlanDetails[]): SubscriptionPlanDetails[] {
  const orderMap: Record<string, number> = {
    reset_table: 0,
    culture_edit: 1,
    all_access: 2,
  };

  return plans.sort((a, b) => {
    const orderA = orderMap[a.id as string] ?? 999;
    const orderB = orderMap[b.id as string] ?? 999;
    return orderA - orderB;
  });
}

/**
 * Add emoji and uppercase to plan name to match webpage
 */
export function formatPlanName(name: string): string {
  return name.toUpperCase()
}

/**
 * Transform API plan response to SubscriptionPlanDetails
 */
export function transformApiPlanToDetails(apiPlan: ApiSubscriptionPlan): SubscriptionPlanDetails {
  const features = extractFeaturesFromApiPlan(apiPlan);
  const isHighlighted = apiPlan.name.includes('All Access');
  const color = getPlanColor(apiPlan.name);
  const formattedName = formatPlanName(apiPlan.name);

  // Extract +1 guest info from the API plan
  const plusOneGuest = extractPlusOneGuestInfo(apiPlan);

  return {
    id: apiPlan.id,
    name: formattedName,
    shortName: getShortName(apiPlan.name),
    description: getDescription(apiPlan.name),
    pricing: {
      monthly: apiPlan.price,
      currency: apiPlan.currency === 'GBP' ? '£' : apiPlan.currency,
    },
    features,
    highlighted: isHighlighted,
    color,
    badge: undefined,
    plusOneGuest,
  };
}
